"use client";

import { useState } from "react";
import Image from "next/image";

// Images
import iso from "@/../public/svgs/heropageLogos/6.webp";
import nba from "@/../public/svgs/heropageLogos/3.svg";
import nba2 from "@/../public/svgs/heropageLogos/4.svg";
import iso2015 from "@/../public/svgs/heropageLogos/7.svg";
import affiliated from "@/../public/svgs/heropageLogos/5.svg";
import bg3 from "@/../public/herosectionImages/hero3.webp";
import bg2 from "@/../public/herosectionImages/heroBg.webp";
import bg1 from "@/../public/herosectionImages/herosection1.webp";

// Posters
import poster1 from "@/../public/posters/1 (1).jpeg";
import poster2 from "@/../public/posters/1 (2).jpeg";
import poster3 from "@/../public/posters/1 (3).jpeg";
import poster4 from "@/../public/posters/1 (4).jpeg";
import poster5 from "@/../public/posters/1 (5).jpeg";
import poster6 from "@/../public/posters/1 (6).jpeg";
import poster7 from "@/../public/posters/1 (7).jpeg";
import poster8 from "@/../public/posters/1 (8).jpeg";
import poster9 from "@/../public/posters/1 (9).jpeg";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";

export interface HomePageImage {
  id: string | number;
  desktopUrl?: string | null;
  mobileUrl?: string | null;
}

interface HeroSectionProps {
  images?: HomePageImage[];
}

export interface LogoMarqueeItem {
  label: string;
  src: any;
  pdfUrl?: string;
  isPoster?: boolean;
}

const HeroSection = ({ images = [] }: HeroSectionProps) => {
  const [selectedImage, setSelectedImage] = useState<LogoMarqueeItem | null>(null);

  const logos: LogoMarqueeItem[] = [
    {
      label: "NAAC Accredited",
      src: nba,
      pdfUrl: "https://apiserver.cec.edu.in/files/NAAC%20Certificate",
    },
    { label: "Campus Highlight", src: poster1, isPoster: true },
    {
      label: "ISO 21001:2018",
      src: iso,
      pdfUrl: "/mandatory/iso/ISO New certificate- 1 21001 EOMS.pdf",
    },
    { label: "Event Announcement", src: poster2, isPoster: true },
    {
      label: "ISO 9001:2015",
      src: iso2015,
      pdfUrl: "/mandatory/iso/ISO New certificate -I 9001 QMS.pdf",
    },
    { label: "Student Activities", src: poster3, isPoster: true },
    {
      label: "NBA Accredited",
      src: nba2,
      pdfUrl: "https://apiserver.cec.edu.in/files/NBA%20certificate%202025",
    },
    { label: "Campus Update", src: poster4, isPoster: true },
    {
      label: "Affiliated to VTU",
      src: affiliated,
      pdfUrl: "https://apiserver.cec.edu.in/files/VTU%20Affiliation",
    },
    { label: "Placement Drive", src: poster5, isPoster: true },
    {
      label: "NAAC Accredited",
      src: nba,
      pdfUrl: "https://apiserver.cec.edu.in/files/NAAC%20Certificate",
    },
    { label: "Technical Fest", src: poster6, isPoster: true },
    {
      label: "ISO 21001:2018",
      src: iso,
      pdfUrl: "/mandatory/iso/ISO New certificate- 1 21001 EOMS.pdf",
    },
    { label: "Cultural Fest", src: poster7, isPoster: true },
    {
      label: "ISO 9001:2015",
      src: iso2015,
      pdfUrl: "/mandatory/iso/ISO New certificate -I 9001 QMS.pdf",
    },
    { label: "Sports Meet", src: poster8, isPoster: true },
    {
      label: "NBA Accredited",
      src: nba2,
      pdfUrl: "https://apiserver.cec.edu.in/files/NBA%20certificate%202025",
    },
    { label: "Special Event", src: poster9, isPoster: true },
  ];

  const backgroundSlides = [
    { type: "static", desktopSrc: bg3, mobileSrc: bg3 },
    { type: "static", desktopSrc: bg1, mobileSrc: bg1 },
    { type: "static", desktopSrc: bg2, mobileSrc: bg2 },
  ];

  return (
    <section className="relative w-full h-[calc(100dvh-100px)] min-h-[520px] md:h-[80vh] flex flex-col justify-center items-center text-center px-4 bg-[#144A72]">
      {/* Background Swiper */}
      <div className="absolute inset-0 z-0">
        <Swiper
          modules={[Autoplay, EffectFade]}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop={true}
          effect="fade"
          className="w-full h-full"
        >
          {backgroundSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              {/* Desktop Image */}
              {slide.desktopSrc && (
                <Image
                  src={slide.desktopSrc}
                  alt={`Desktop Background ${index + 1}`}
                  fill
                  className="object-cover hidden lg:block -translate-y-[50px] lg:-translate-y-[30px]"
                  priority={index === 0}
                />
              )}

              {/* Mobile Image */}
              {slide.mobileSrc && (
                <Image
                  src={slide.mobileSrc}
                  alt={`Mobile Background ${index + 1}`}
                  fill
                  className="object-cover block lg:hidden object-center"
                  priority={index === 0}
                />
              )}

              <div className="absolute bottom-0 left-0 right-0 h-[220px] md:h-[180px] bg-gradient-to-t from-white/90 via-white/40 to-transparent z-[10] block md:hidden pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 h-[250px] md:h-[180px] bg-gradient-to-t from-[#fcfdff] via-white/70 to-transparent z-[10] hidden md:block pointer-events-none" />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-b from-transparent to-white/30 pointer-events-none"></div>
      </div>

      {/* Hero Header Text Overlay */}
      <div className="absolute top-8 sm:top-6 left-0 right-0 z-10 max-w-5xl mx-auto px-4 text-center md:relative md:top-auto md:-translate-y-36 lg:-translate-y-44">
        <h1 className="text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] font-bold text-white tracking-tight drop-shadow-lg">
          Canara Engineering <br className="block sm:hidden" />College
        </h1>
      </div>

      {/* Modern Compact Logos & Posters Marquee */}
      <div className="absolute bottom-2 md:bottom-0 md:translate-y-16 lg:translate-y-24 left-0 right-0 mx-auto w-[95%] max-w-7xl z-20 bg-white/90 backdrop-blur-md rounded-2xl border border-white/60 shadow-xl px-2.5 py-2.5 md:px-5 md:py-4 transition-all">
        <style jsx global>{`
          .marquee-swiper .swiper-wrapper {
            transition-timing-function: linear !important;
          }
        `}</style>
        <Swiper
          modules={[Autoplay]}
          autoplay={{
            delay: 0,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          speed={5000}
          loop={true}
          slidesPerView={2.2}
          spaceBetween={10}
          breakpoints={{
            480: { slidesPerView: 2.5, spaceBetween: 14 },
            640: { slidesPerView: 3.4, spaceBetween: 18 },
            768: { slidesPerView: 4.2, spaceBetween: 20 },
            1024: { slidesPerView: 5.0, spaceBetween: 22 },
            1280: { slidesPerView: 5.5, spaceBetween: 24 },
          }}
          className="marquee-swiper w-full"
        >
          {logos.map((item, index) => (
            <SwiperSlide key={index}>
              {item.isPoster ? (
                /* Poster Item (Wider & Taller Poster Card) */
                <div
                  onClick={() => setSelectedImage(item)}
                  className="relative flex flex-col items-center justify-center cursor-pointer group py-0.5"
                >
                  <div className="relative h-[140px] w-[115px] sm:h-[195px] sm:w-[155px] md:w-[170px] rounded-xl overflow-hidden shadow-md group-hover:shadow-2xl transition-all duration-300 group-hover:scale-108 group-hover:-translate-y-1.5 bg-slate-900/5 border border-white/80 ring-1 ring-slate-900/5">
                    <Image
                      src={item.src}
                      alt={item.label || "Campus Poster"}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-1.5 sm:p-2">
                      <span className="text-[10px] sm:text-xs font-semibold text-white flex items-center gap-1 bg-black/60 backdrop-blur-xs px-2 py-0.5 rounded-full shadow-sm">
                        View Poster ↗
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Accreditation / Logo Item (Matching Poster Dimensions) */
                <div
                  onClick={() => setSelectedImage(item)}
                  className="relative flex flex-col items-center justify-center cursor-pointer group py-0.5"
                >
                  <div className="relative h-[140px] w-full sm:h-[195px] max-w-[170px] rounded-xl overflow-hidden shadow-md group-hover:shadow-2xl transition-all duration-300 group-hover:scale-108 group-hover:-translate-y-1.5 bg-white/90 group-hover:bg-white border border-slate-200/80 p-2 sm:p-3 flex flex-col items-center justify-between">
                    <div className="flex-1 w-full flex items-center justify-center">
                      <Image
                        src={item.src}
                        alt={item.label}
                        width={150}
                        height={115}
                        className="max-h-[85px] sm:max-h-[125px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <p className="text-[10px] sm:text-xs font-semibold text-slate-800 text-center line-clamp-1 group-hover:text-blue-700 transition-colors pt-1 border-t border-slate-100/80 w-full">
                      {item.label}
                    </p>
                  </div>
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Popup Modal for Certificate or Poster Preview */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 md:p-6 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl p-4 md:p-6 flex flex-col items-center justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors shadow cursor-pointer"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {selectedImage.pdfUrl ? (
              <div className="w-full h-[80vh] flex flex-col">
                <h3 className="text-xl font-bold text-[#192f59] mb-3 pr-10 border-b pb-2 text-left">
                  {selectedImage.label ? `${selectedImage.label} Certificate` : "Certificate"}
                </h3>
                <div className="w-full flex-1 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 relative">
                  <iframe
                    src={selectedImage.pdfUrl}
                    className="w-full h-full border-0 rounded-xl"
                    title={selectedImage.label || "Certificate PDF"}
                  />
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-2">
                <div className="relative w-full flex-1 max-h-[82vh] flex items-center justify-center overflow-hidden">
                  <Image
                    src={selectedImage.src}
                    alt="Poster Preview"
                    className="max-h-[82vh] w-auto h-auto object-contain rounded-xl shadow-lg"
                    priority
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;